import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { AppShell } from '../design-system/shell/app-shell';
import { NavIdentity, type NavEntry } from '../design-system/shell/nav';
import { PageHeader } from '../design-system/shell/page-header';
import { ActionButton } from '../design-system/concept/action-button';
import { CoachCard } from '../design-system/concept/coach-card';
import { Eyebrow } from '../design-system/concept/eyebrow';
import { ClientsIcon, SettingsIcon } from '../design-system/shell/icons';
import { useToast } from '../design-system/primitives/use-toast';

import { AuthProvider } from '../lib/http-service';
import { useAuth } from '../context/AuthContext';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { useImpersonation } from '../context/ImpersonationContext';
import { getTrainers, getTrainerClients } from '../api/admin';
import type { ClientData } from '../lib/interfaces';

const NAV: NavEntry[] = [
  { key: 'accounts', label: 'Accounts', Icon: ClientsIcon },
  { key: 'settings', label: 'Settings', Icon: SettingsIcon, footer: true, desktopOnly: true },
];

/**
 * Superadmin: browse every trainer account and view the app as one of them.
 *
 * Reachable by URL only, and only by an actual superadmin. Both halves matter
 * and they are enforced in different places:
 *
 *   - No surface links here. There is no nav entry, no button and no menu
 *     item anywhere in the app; `/superadmin` appears in App.tsx and nowhere
 *     else. e2e/pt/superadmin.spec.ts asserts that, so a helpful link added
 *     later fails the build rather than quietly exposing the route.
 *
 *   - The role check is the server's. `useIsSuperAdmin` does not read a claim
 *     or a flag the client could set; it calls GET superadmin/accounts and
 *     believes the response. A non-superadmin gets 403 from the API whatever
 *     the front end thinks, so the screen below is a convenience, not the
 *     control.
 *
 * The denial state is deliberately vague. Naming the capability would confirm
 * to anyone who guessed the URL that it is worth guessing again.
 */
const SuperAdminPage = () => {
  const history = useHistory();
  const { getAccessTokenSilently, user } = useAuth();
  const [auth] = useState(() => new AuthProvider(getAccessTokenSilently));
  const { isSuperAdmin, loading } = useIsSuperAdmin(auth);
  const { impersonating, setImpersonating } = useImpersonation();
  const { toast } = useToast();

  const [trainers, setTrainers] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin || loading) return;
    setLoadingTrainers(true);
    getTrainers(auth)
      .then(setTrainers)
      .catch(() =>
        toast({
          title: 'Could not load accounts',
          description: 'The accounts list did not come back.',
          variant: 'destructive',
        }),
      )
      .finally(() => setLoadingTrainers(false));
  }, [isSuperAdmin, loading]);

  useEffect(() => {
    if (!selected) {
      setClients([]);
      return;
    }
    setLoadingClients(true);
    getTrainerClients(selected, auth)
      .then(setClients)
      .catch(() =>
        toast({
          title: 'Could not load clients',
          description: `No client list came back for ${selected}.`,
          variant: 'destructive',
        }),
      )
      .finally(() => setLoadingClients(false));
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? trainers.filter((t) => t.toLowerCase().includes(q)) : trainers;
  }, [trainers, query]);

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-shell font-sans text-sm text-muted-foreground">
        Checking access…
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-shell p-4 font-sans">
        <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8">
          <Eyebrow>Sizewall</Eyebrow>
          <h1 className="mt-2 text-xl font-semibold tracking-tight">Not available</h1>
          <p className="mt-1 text-sm text-foreground-soft">
            This area is not available for your account.
          </p>
          <ActionButton className="mt-6" onClick={() => history.push('/clients')}>
            Back to clients
          </ActionButton>
        </div>
      </div>
    );
  }

  return (
    <AppShell
      entries={NAV}
      active="accounts"
      onSelect={() => undefined}
      onHome={() => history.push('/overview')}
      hidePhoneTabs
      phoneBack={() => history.push('/clients')}
      phoneTitle="Accounts"
      identity={
        <NavIdentity
          name={user?.name ?? 'Superadmin'}
          // Just the role: the account count is already in the page meta, and
          // the rail truncates at 200px wide.
          detail="Superadmin"
          imageUrl={user?.picture}
        />
      }
    >
      <div className="mx-auto flex max-w-[1296px] flex-col gap-3.5 p-4 lg:p-7">
        <PageHeader
          eyebrow="Superadmin"
          title="Accounts"
          meta={loadingTrainers ? undefined : `${trainers.length} trainer accounts`}
          actions={
            <ActionButton onClick={() => history.push('/clients')}>
              Back to clients
            </ActionButton>
          }
        />

        {impersonating && (
          <div className="projected-frame flex items-center justify-between gap-3 bg-[hsl(var(--projected-bg))] p-3.5">
            <div>
              <Eyebrow tone="projected">Impersonating</Eyebrow>
              <div className="mt-1 text-[12.5px] font-medium">
                Viewing the app as {impersonating}
              </div>
            </div>
            <ActionButton
              onClick={() => {
                setImpersonating(null);
                setSelected(null);
                toast({ title: 'Stopped impersonating' });
              }}
            >
              Stop
            </ActionButton>
          </div>
        )}

        <div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <CoachCard testId="trainer-list" title={`Trainers · ${trainers.length}`}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email"
              aria-label="Search trainers"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
            />
            {loadingTrainers ? (
              <p className="text-sm text-muted-foreground">Loading accounts…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">No account matches that search.</p>
            ) : (
              <div className="flex max-h-[420px] flex-col overflow-y-auto">
                {filtered.map((email, i) => (
                  <div
                    key={email}
                    className={`flex items-center justify-between gap-3 py-2.5 ${
                      i < filtered.length - 1 ? 'border-b border-divider' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelected(email)}
                      className={`min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-xs ${
                        selected === email
                          ? 'bg-accent font-semibold text-accent-foreground'
                          : 'hover:bg-shell'
                      }`}
                    >
                      {email}
                    </button>
                    <ActionButton
                      onClick={() => {
                        setImpersonating(email);
                        toast({ title: 'Impersonating', description: `Now viewing as ${email}` });
                        history.push('/clients');
                      }}
                    >
                      View as
                    </ActionButton>
                  </div>
                ))}
              </div>
            )}
          </CoachCard>

          <CoachCard
            testId="trainer-clients"
            title={selected ? `Clients of ${selected}` : 'Clients'}
          >
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Pick a trainer to see their clients.
              </p>
            ) : loadingClients ? (
              <p className="text-sm text-muted-foreground">Loading clients…</p>
            ) : clients.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This trainer has no clients.
              </p>
            ) : (
              <div className="flex max-h-[420px] flex-col overflow-y-auto">
                {clients.map((client, i) => (
                  <div
                    key={client.ID}
                    className={`flex items-center justify-between gap-3 py-2.5 ${
                      i < clients.length - 1 ? 'border-b border-divider' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold">
                        {client.firstName} {client.lastName}
                      </span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {client.email}
                      </span>
                    </span>
                    <span className="tabular shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                      {client.age ? `${client.age} y` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CoachCard>
        </div>
      </div>
    </AppShell>
  );
};

export default SuperAdminPage;
